import { useState, useMemo, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Button,
    Input,
    Textarea,
    Autocomplete,
    AutocompleteItem,
    Divider,
    Modal,
    ModalContent,
    ModalBody,
    ModalFooter,
    useDisclosure,
    Image,
    Chip,
    DatePicker,
    NumberInput,
    Drawer,
    DrawerContent,
    DrawerHeader,
    DrawerBody,
    DrawerFooter,
    Skeleton,
    Spinner,
} from "@heroui/react";
import { today, getLocalTimeZone, parseDate } from "@internationalized/date";
import {
    LuArrowLeft,
    LuShoppingCart,
    LuTrash2,
    LuPackage,
    LuSearch,
    LuPencil,
} from "react-icons/lu";
import { GiChicken } from "react-icons/gi";
import { useForm, Controller } from "react-hook-form";
import { useFetchData, usePostData } from "@/hooks";
import { useUser } from "@/context/UserContext";
import {
    PageHeader,
    ThemedText,
    EmptyState,
    ErrorState,
    NoImageFallback,
    CreatableAutocomplete,
} from "@/components/ui";
import {
    formatCurrency,
    formatPrice,
    formatThousands,
    findOption,
    showToast,
} from "@/utils";

// Referensi stabil: NumberInput mereset teks input bila objek formatOptions berganti
const PRICE_FORMAT_OPTIONS = { style: "decimal", maximumFractionDigits: 2 };
const SUBTOTAL_FORMAT_OPTIONS = { style: "decimal", maximumFractionDigits: 0 };

// Subtotal baris (sebelum diskon) dalam rupiah bulat
const calcSubtotal = (quantity, price) =>
    Math.round((Number(quantity) || 0) * (Number(price) || 0));

// Harga satuan turunan dari subtotal, dibulatkan 2 desimal
const calcPriceFromSubtotal = (subtotal, quantity) => {
    const qty = Number(quantity) || 0;
    if (qty <= 0) return null;
    return Math.round(((Number(subtotal) || 0) / qty) * 100) / 100;
};

const SalesOrderCreate = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { hasPermission } = useUser();
    const isEdit = !!id;

    // -- Form State --
    const {
        control,
        handleSubmit,
        watch,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {
            order_date: today(getLocalTimeZone()),
            due_date: null,
            customer_id: "",
            payment_method_id: "",
            master_cash_id: "",
            giro: {
                pickup_date: null,
                bank_id: "",
            },
            description: "",
        },
    });

    const paymentMethodId = watch("payment_method_id");

    // -- Data Fetching --
    const fetchCustomers = useFetchData("select/customer");
    const fetchPaymentMethods = useFetchData("select/payment-method");
    const fetchBanks = useFetchData("select/bank");
    const fetchMasterCash = useFetchData("select/master-cash");

    // Cash/Transfer/QRIS = langsung bayar, perlu pilih jenis kas
    const isCashOrTransfer = ["1", "2", "3"].includes(String(paymentMethodId));
    const fetchMaterials = useFetchData("select/sales-order-material");

    // Fetch initial data if editing
    const fetchUrl = isEdit ? `sales-orders/${id}/edit` : null;
    const {
        data: initialData,
        isLoading: isFetching,
        error: fetchError,
        refetch,
    } = useFetchData(fetchUrl);

    const endpoint = isEdit
        ? `sales-orders/${id}/update`
        : "sales-orders/store";
    const storeSalesOrder = usePostData(endpoint);

    // Populate form and cart when editing
    useEffect(() => {
        if (isEdit && initialData) {
            // Guard: block edit if payment is paid and user lacks edit-paid permission
            const isPaid = [3, 4].includes(initialData.payment_status_id);
            if (isPaid && !hasPermission("sales-order.edit-paid")) {
                showToast(
                    "error",
                    "Tidak Bisa Diedit",
                    `Sales Order yang sudah ${initialData.payment_status?.name?.toLowerCase() || "lunas"} tidak dapat diubah.`,
                );
                navigate("/sales-order");
                return;
            }

            // 1. Reset Form
            reset({
                order_date: initialData.order_date
                    ? parseDate(initialData.order_date)
                    : today(getLocalTimeZone()),
                due_date: initialData.due_date
                    ? parseDate(initialData.due_date)
                    : null,
                customer_id: String(initialData.customer_id),
                payment_method_id: String(initialData.payment_method_id),
                description: initialData.description || "",
                // TODO: Handle Giro fields if payment_method is Giro (needs journal relations or saved Data)
            });

            // 2. Populate Cart
            if (initialData.items && initialData.items.length > 0) {
                const mappedItems = initialData.items.map((item, idx) => {
                    const quantity = parseFloat(item.quantity);
                    const price =
                        parseFloat(item.unit_price) ||
                        parseFloat(item.price) ||
                        0; // Handle DB column name

                    return {
                        cart_id: Date.now() + idx + Math.random(), // Unique ID for cart operations
                        id: item.material_id, // Important: ID for material lookup
                        name: item.material?.name || "Unknown Item",
                        alias: item.alias || null,
                        quantity,
                        unit_id: item.material_unit_id,
                        unit: item.unit?.name || "",
                        price,
                        // Subtotal baris = sumber kebenaran nilai uang
                        subtotal:
                            parseFloat(item.subtotal) ||
                            calcSubtotal(quantity, price),
                        description: item.description,
                        warehouse_id: item.warehouse_id,
                        available_stock: 9999, // Hack: Stock info might be missing in SI Detail
                        image_url: item.material?.image_url,
                    };
                });
                setCart(mappedItems);
            }
        }
    }, [isEdit, initialData, reset]);

    // -- Cart State --
    const [cart, setCart] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");

    // -- Warehouse Stock State --
    const [warehouseStockUrl, setWarehouseStockUrl] = useState(null);
    const fetchWarehouseStock = useFetchData(warehouseStockUrl);

    // -- Modal & Drawer State --
    const itemModal = useDisclosure();
    const cartDrawer = useDisclosure();
    const [selectedItem, setSelectedItem] = useState(null);

    // -- Modal Item Form (separate from main form) --
    const {
        control: itemControl,
        handleSubmit: handleItemSubmit,
        watch: watchItem,
        getValues: getItemValues,
        setValue: setItemValue,
        reset: resetItemForm,
        formState: { errors: itemErrors },
    } = useForm({
        defaultValues: {
            alias: "",
            quantity: 1,
            unit_id: null,
            unit: "",
            price: 0,
            subtotal: 0,
            total_birds: "",
            warehouse_id: null,
            available_stock: 0,
        },
    });

    const itemFormValues = watchItem();

    // -- Derived Data --
    const filteredMaterials = useMemo(() => {
        if (!fetchMaterials.data) return [];
        return fetchMaterials.data.filter((item) =>
            item.name.toLowerCase().includes(searchQuery.toLowerCase()),
        );
    }, [fetchMaterials.data, searchQuery]);

    const totalAmount = useMemo(() => {
        return cart.reduce(
            (sum, item) => sum + (Number(item.subtotal) || 0),
            0,
        );
    }, [cart]);

    // -- Handlers --
    const handleItemClick = (item) => {
        setSelectedItem(item);
        // Find default unit or first available
        const defaultUnit =
            item.units?.find((u) => u.is_default) || item.units?.[0];

        // Fetch warehouse stock for this material + unit
        if (item.id && defaultUnit?.unit_id) {
            setWarehouseStockUrl(
                `select/warehouse-stock?material_id=${item.id}&unit_id=${defaultUnit.unit_id}`,
            );
        } else {
            setWarehouseStockUrl(null);
        }

        const defaultPrice = defaultUnit?.price || item.price || 0;

        // Reset modal form with new values
        resetItemForm({
            alias: "",
            quantity: 1,
            unit_id: defaultUnit?.unit_id || null,
            unit: defaultUnit?.unit || item.unit || "",
            price: defaultPrice,
            subtotal: calcSubtotal(1, defaultPrice),
            total_birds: "",
            warehouse_id: null,
            available_stock: 0,
        });
        itemModal.onOpen();
    };

    const handleUnitChange = (unitId) => {
        if (!selectedItem?.units) return;
        const unit = selectedItem.units.find(
            (u) => String(u.unit_id) === String(unitId),
        );
        if (unit) {
            // Refetch warehouse stock for new unit
            setWarehouseStockUrl(
                `select/warehouse-stock?material_id=${selectedItem.id}&unit_id=${unit.unit_id}`,
            );

            setItemValue("unit_id", unit.unit_id);
            setItemValue("unit", unit.unit);
            setItemValue("price", unit.price || 0);
            setItemValue(
                "subtotal",
                calcSubtotal(getItemValues("quantity"), unit.price || 0),
            );
            setItemValue("warehouse_id", null); // Reset warehouse when unit changes
            setItemValue("available_stock", 0);
        }
    };

    // Sinkronisasi dua arah Jumlah/Harga ↔ Subtotal.
    // Dilakukan di handler perubahan (bukan effect) agar tidak saling memicu.
    const handleQuantityValueChange = (value) => {
        if (!Number.isFinite(value)) return;
        setItemValue("subtotal", calcSubtotal(value, getItemValues("price")));
    };

    const handlePriceValueChange = (value) => {
        if (!Number.isFinite(value)) return;
        setItemValue(
            "subtotal",
            calcSubtotal(getItemValues("quantity"), value),
        );
    };

    const handleSubtotalValueChange = (value) => {
        if (!Number.isFinite(value)) return;
        const price = calcPriceFromSubtotal(value, getItemValues("quantity"));
        if (price !== null) setItemValue("price", price);
    };

    // Helper to calculate real stock (Available - In Cart)
    const getRealStock = (warehouseId, originalStock) => {
        if (!selectedItem || !warehouseId) return originalStock;

        // Find usage in cart for same Material + Unit + Warehouse
        const cartQty = cart.reduce((acc, item) => {
            if (
                item.id === selectedItem.id &&
                String(item.unit_id) === String(itemFormValues.unit_id) &&
                String(item.warehouse_id) === String(warehouseId) &&
                item.cart_id !== selectedItem.cart_id // Exclude self if in Edit Mode
            ) {
                return acc + item.quantity;
            }
            return acc;
        }, 0);

        return Math.max(0, originalStock - cartQty);
    };

    const handleWarehouseChange = (warehouseId) => {
        if (!warehouseId) {
            setItemValue("warehouse_id", null);
            setItemValue("available_stock", 0);
            setItemValue("warehouse", "");
            return;
        }

        const warehouse = findOption(fetchWarehouseStock.data, warehouseId);
        const realStock = getRealStock(warehouseId, warehouse?.stock || 0);

        setItemValue("warehouse_id", warehouseId);
        setItemValue("available_stock", realStock);
        setItemValue("warehouse", warehouse?.title || "");
    };

    // Form submit handler for modal (receives validated data)
    const onItemSubmit = (data) => {
        if (!selectedItem) return;

        // Additional validation: quantity vs stock
        if (data.quantity > data.available_stock) {
            showToast(
                "error",
                "Stok Tidak Cukup",
                `Stok tersedia hanya ${data.available_stock} ${data.unit}.`,
            );
            return;
        }

        const description = data.total_birds ? `${data.total_birds} Ekor` : "";

        const alias = data.alias?.trim() || "";

        const price = parseFloat(data.price);
        const subtotal = Math.round(Number(data.subtotal) || 0);

        setCart((prev) => {
            // Case 1: Edit existing item in cart (has cart_id)
            if (selectedItem.cart_id) {
                return prev.map((item) =>
                    item.cart_id === selectedItem.cart_id
                        ? {
                              ...selectedItem,
                              alias: alias,
                              quantity: parseFloat(data.quantity),
                              unit_id: data.unit_id,
                              unit: data.unit,
                              price,
                              subtotal,
                              description: description,
                              total_birds: data.total_birds,
                              warehouse_id: data.warehouse_id,
                              available_stock: data.available_stock,
                              warehouse: data.warehouse,
                          }
                        : item,
                );
            }

            // Case 2: Add new item, check for duplicates (same id, unit, warehouse, alias)
            const duplicateIndex = prev.findIndex(
                (item) =>
                    item.id === selectedItem.id &&
                    String(item.unit_id) === String(data.unit_id) &&
                    String(item.warehouse_id) === String(data.warehouse_id) &&
                    parseFloat(item.price) === price &&
                    (item.alias || "") === alias,
            );

            if (duplicateIndex >= 0) {
                // Accumulate quantity & subtotal
                const newCart = [...prev];
                const newQuantity =
                    newCart[duplicateIndex].quantity +
                    parseFloat(data.quantity);

                // Validate accumulated stock
                if (newQuantity > data.available_stock) {
                    showToast(
                        "error",
                        "Stok Tidak Cukup",
                        `Total quantity (${newQuantity}) melebihi stok tersedia (${data.available_stock}).`,
                    );
                    return prev; // Cancel update
                }

                newCart[duplicateIndex] = {
                    ...newCart[duplicateIndex],
                    quantity: newQuantity,
                    subtotal:
                        (Number(newCart[duplicateIndex].subtotal) || 0) +
                        subtotal,
                    // Update other fields to latest input just in case
                    price,
                    description:
                        description || newCart[duplicateIndex].description,
                    total_birds:
                        data.total_birds || newCart[duplicateIndex].total_birds,
                };
                return newCart;
            }

            // Case 3: Add new unique item
            const newItem = {
                ...selectedItem,
                cart_id: Date.now() + Math.random(), // Generate temporary unique ID
                alias: alias,
                quantity: parseFloat(data.quantity),
                unit_id: data.unit_id,
                unit: data.unit,
                price,
                subtotal,
                description: description,
                total_birds: data.total_birds,
                warehouse_id: data.warehouse_id,
                available_stock: data.available_stock,
                warehouse: data.warehouse,
            };
            return [...prev, newItem];
        });

        itemModal.onClose();
        setSelectedItem(null);
    };

    const handleRemoveFromCart = (cartId) => {
        setCart((prev) => prev.filter((item) => item.cart_id !== cartId));
    };

    const handleEditCartItem = (cartItem) => {
        setSelectedItem(cartItem);

        // Fetch warehouse stock for this material + unit
        if (cartItem.id && cartItem.unit_id) {
            setWarehouseStockUrl(
                `select/warehouse-stock?material_id=${cartItem.id}&unit_id=${cartItem.unit_id}`,
            );
        }

        // Reset form with cart item values
        resetItemForm({
            alias: cartItem.alias || "",
            quantity: cartItem.quantity,
            unit_id: cartItem.unit_id,
            unit: cartItem.unit,
            price: cartItem.price,
            subtotal:
                cartItem.subtotal ??
                calcSubtotal(cartItem.quantity, cartItem.price),
            total_birds: cartItem.total_birds || "",
            warehouse_id: cartItem.warehouse_id || null,
            available_stock: cartItem.available_stock || 0,
        });
        itemModal.onOpen();
    };

    const onSubmit = (data) => {
        if (cart.length === 0) {
            showToast("error", "Error", "Keranjang belanja masih kosong!");
            return;
        }

        const payload = {
            ...data,
            order_date: data.order_date.toString(),
            due_date: data.due_date ? data.due_date.toString() : null,
            sales_order_status_id: 1,
            items: cart.map((item) => ({
                material_id: item.id,
                warehouse_id: item.warehouse_id,
                material_unit_id: item.unit_id,
                alias: item.alias || null,
                quantity: item.quantity,
                price: item.price,
                subtotal: Math.round(Number(item.subtotal) || 0),
                description: item.description,
            })),
        };

        if (isEdit) {
            payload._method = "PUT";
        }

        storeSalesOrder.mutate(payload, {
            onSuccess: () => {
                showToast(
                    "success",
                    "Berhasil",
                    `Penjualan berhasil ${isEdit ? "diperbarui" : "dibuat"}!`,
                );
                navigate("/sales-order");
            },
            onError: (err) => {
                showToast(
                    "error",
                    "Gagal",
                    err.response?.data?.message ||
                        `Gagal ${isEdit ? "memperbarui" : "membuat"} Penjualan`,
                );
            },
        });
    };

    const isLivestock = selectedItem?.category_id === 5;

    if (isEdit && isFetching) {
        return (
            <div className="flex h-[calc(100vh-3rem)] w-full items-center justify-center">
                <Spinner label="Memuat data penjualan..." color="primary" />
            </div>
        );
    }

    if (isEdit && fetchError) {
        return (
            <ErrorState
                title="Gagal Memuat Data"
                description="Terjadi kesalahan saat memuat data. Silakan coba lagi atau hubungi administrator."
                onAction={refetch}
            />
        );
    }

    return (
        <div className="flex w-full flex-col gap-4 md:h-[calc(100vh-3rem)] md:flex-row md:overflow-hidden">
            {/* Left Side: Form & Items */}
            <div className="flex w-full flex-col gap-4 md:flex-1 md:overflow-hidden">
                {/* Header */}
                <div className="flex gap-4">
                    <Button
                        isIconOnly
                        variant="light"
                        onPress={() => navigate("/sales-order")}
                    >
                        <LuArrowLeft size={20} />
                    </Button>
                    <PageHeader
                        title={isEdit ? "Edit Penjualan" : "Tambah Penjualan"}
                        breadcrumbs={[
                            { label: "Dashboard", href: "/" },
                            { label: "Penjualan", href: "/sales-order" },
                            { label: isEdit ? "Edit" : "Tambah" },
                        ]}
                    />
                </div>

                {/* Main Content Card */}
                <Card
                    className="border-default-200 flex flex-col border md:flex-1 md:flex-row md:overflow-hidden"
                    shadow="none"
                >
                    <CardBody className="flex flex-col gap-6 p-4 md:flex-1 md:flex-row md:overflow-hidden">
                        {/* Order Info Section */}
                        <div className="flex w-full flex-col gap-4 md:w-2/5">
                            <ThemedText className="font-semibold">
                                Informasi Order
                            </ThemedText>

                            <div className="flex flex-col gap-4">
                                <Controller
                                    name="order_date"
                                    control={control}
                                    rules={{ required: "Tanggal order wajib diisi" }}
                                    render={({ field }) => (
                                        <DatePicker
                                            label="Tanggal Order"
                                            value={field.value}
                                            onChange={field.onChange}
                                            variant="bordered"
                                            isRequired
                                            showMonthAndYearPickers
                                        />
                                    )}
                                />

                                <Controller
                                    name="due_date"
                                    control={control}
                                    render={({ field }) => (
                                        <DatePicker
                                            label="Jatuh Tempo"
                                            value={field.value}
                                            onChange={field.onChange}
                                            variant="bordered"
                                            showMonthAndYearPickers
                                        />
                                    )}
                                />

                                <Controller
                                    name="customer_id"
                                    control={control}
                                    rules={{
                                        required: "Pelanggan wajib dipilih",
                                    }}
                                    render={({ field }) => (
                                        <Autocomplete
                                            label="Pelanggan"
                                            placeholder="Cari pelanggan..."
                                            variant="bordered"
                                            isRequired
                                            defaultItems={
                                                fetchCustomers.data || []
                                            }
                                            selectedKey={field.value}
                                            onSelectionChange={field.onChange}
                                            isLoading={fetchCustomers.isLoading}
                                            isInvalid={!!errors.customer_id}
                                            errorMessage={
                                                errors.customer_id?.message
                                            }
                                        >
                                            {(item) => (
                                                <AutocompleteItem
                                                    key={item.value}
                                                >
                                                    {item.title}
                                                </AutocompleteItem>
                                            )}
                                        </Autocomplete>
                                    )}
                                />

                                <Controller
                                    name="payment_method_id"
                                    control={control}
                                    rules={{
                                        required:
                                            "Metode pembayaran wajib dipilih",
                                    }}
                                    render={({ field }) => (
                                        <CreatableAutocomplete
                                            label="Metode Pembayaran"
                                            placeholder="Pilih metode pembayaran"
                                            variant="bordered"
                                            isRequired
                                            items={
                                                fetchPaymentMethods.data || []
                                            }
                                            selectedKey={field.value}
                                            onSelectionChange={field.onChange}
                                            storeUrl="payment-method/store"
                                            isLoading={
                                                fetchPaymentMethods.isLoading
                                            }
                                            isInvalid={
                                                !!errors.payment_method_id
                                            }
                                            errorMessage={
                                                errors.payment_method_id
                                                    ?.message
                                            }
                                            description={
                                                isCashOrTransfer
                                                    ? "Pembayaran otomatis terbuat — status otomatis lunas"
                                                    : undefined
                                            }
                                        />
                                    )}
                                />

                                {/* Conditional Master Cash for Cash/Transfer/QRIS */}
                                {isCashOrTransfer && (
                                    <Controller
                                        name="master_cash_id"
                                        control={control}
                                        rules={{
                                            required: "Tujuan kas wajib dipilih",
                                        }}
                                        render={({ field }) => (
                                            <Autocomplete
                                                label="Tujuan Kas"
                                                placeholder="Pilih tujuan kas"
                                                variant="bordered"
                                                isRequired
                                                selectedKey={field.value}
                                                onSelectionChange={field.onChange}
                                                isLoading={fetchMasterCash.isLoading}
                                                isInvalid={!!errors.master_cash_id}
                                                errorMessage={errors.master_cash_id?.message}
                                            >
                                                {(fetchMasterCash.data || []).map((item) => (
                                                    <AutocompleteItem key={item.value}>
                                                        {item.title}
                                                    </AutocompleteItem>
                                                ))}
                                            </Autocomplete>
                                        )}
                                    />
                                )}

                                {/* Conditional Giro Fields */}
                                {String(paymentMethodId) === "7" && (
                                    <div className="flex flex-col gap-4">
                                        <div className="flex items-center gap-2">
                                            <Divider className="flex-1" />
                                            <ThemedText size="sm">
                                                Informasi Giro
                                            </ThemedText>
                                            <Divider className="flex-1" />
                                        </div>
                                        <Controller
                                            name="giro.pickup_date"
                                            control={control}
                                            rules={{
                                                required:
                                                    "Tanggal giro wajib diisi",
                                            }}
                                            render={({ field }) => (
                                                <DatePicker
                                                    label="Tanggal Jatuh Tempo"
                                                    variant="bordered"
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    isInvalid={
                                                        !!errors.giro
                                                            ?.pickup_date
                                                    }
                                                    errorMessage={
                                                        errors.giro?.pickup_date
                                                            ?.message
                                                    }
                                                    showMonthAndYearPickers
                                                />
                                            )}
                                        />
                                        <Controller
                                            name="giro.bank_id"
                                            control={control}
                                            rules={{
                                                required: "Bank wajib dipilih",
                                            }}
                                            render={({ field }) => (
                                                <CreatableAutocomplete
                                                    label="Bank"
                                                    placeholder="Pilih bank"
                                                    variant="bordered"
                                                    items={fetchBanks.data || []}
                                                    selectedKey={field.value}
                                                    onSelectionChange={field.onChange}
                                                    storeUrl="bank/store"
                                                    isLoading={fetchBanks.isLoading}
                                                    isInvalid={!!errors.giro?.bank_id}
                                                    errorMessage={errors.giro?.bank_id?.message}
                                                />
                                            )}
                                        />
                                    </div>
                                )}

                                {/* Catatan */}
                                <Controller
                                    name="description"
                                    control={control}
                                    render={({ field }) => (
                                        <Textarea
                                            label="Catatan (Opsional)"
                                            placeholder="Catatan tambahan..."
                                            variant="bordered"
                                            minRows={3}
                                            maxRows={6}
                                            value={field.value}
                                            onValueChange={field.onChange}
                                        />
                                    )}
                                />
                            </div>
                        </div>

                        {/* Product Grid Section */}
                        <div className="flex flex-1 flex-col gap-4 overflow-hidden">
                            <div className="flex items-center justify-between">
                                <ThemedText className="font-semibold">
                                    Pilih Produk
                                </ThemedText>
                                <div className="w-64">
                                    <Input
                                        placeholder="Cari produk..."
                                        startContent={<LuSearch />}
                                        size="sm"
                                        value={searchQuery}
                                        onValueChange={setSearchQuery}
                                    />
                                </div>
                            </div>

                            <div className="grid flex-1 auto-rows-min grid-cols-2 gap-3 overflow-y-auto pr-1 pb-4 sm:grid-cols-3 2xl:grid-cols-4">
                                {fetchMaterials.isLoading ? (
                                    [...Array(8)].map((_, index) => (
                                        <Card
                                            key={`skeleton-${index}`}
                                            className="border-default-200 w-full border"
                                            shadow="none"
                                            radius="sm"
                                        >
                                            <CardBody className="overflow-hidden p-0">
                                                <Skeleton className="aspect-square w-full rounded-none" />
                                                <div className="flex w-full flex-col gap-2 p-2">
                                                    <Skeleton className="h-4 w-3/4 rounded" />
                                                    <Skeleton className="h-3 w-1/2 rounded" />
                                                    <Skeleton className="h-4 w-2/3 rounded" />
                                                </div>
                                            </CardBody>
                                        </Card>
                                    ))
                                ) : filteredMaterials.length > 0 ? (
                                    filteredMaterials.map((item) => (
                                        <Card
                                            key={item.id}
                                            isPressable
                                            onPress={() =>
                                                handleItemClick(item)
                                            }
                                            className="hover:border-primary-500 border-default-200 w-full border transition-all hover:shadow-sm"
                                            shadow="none"
                                            radius="sm"
                                        >
                                            <CardBody className="overflow-hidden p-0">
                                                {item.image_url ? (
                                                    <Image
                                                        width="100%"
                                                        alt={item.name}
                                                        className="aspect-square w-full object-cover"
                                                        src={item.image_url}
                                                        radius="none"
                                                    />
                                                ) : (
                                                    <div className="bg-default-100 flex aspect-square w-full items-center justify-center">
                                                        <NoImageFallback
                                                            iconSize={32}
                                                            icon={GiChicken}
                                                        />
                                                    </div>
                                                )}
                                                <div className="flex w-full flex-col items-start gap-0.5 p-2">
                                                    <ThemedText
                                                        size="sm"
                                                        className="line-clamp-2 p-0 font-semibold"
                                                    >
                                                        {item.name}
                                                    </ThemedText>
                                                    <ThemedText
                                                        size="xs"
                                                        variant="secondary"
                                                        className="line-clamp-1"
                                                    >
                                                        {item.category} -{" "}
                                                        {item.sub_category}
                                                    </ThemedText>
                                                    <div className="flex w-full items-center">
                                                        <ThemedText
                                                            size="sm"
                                                            variant="primary"
                                                            className="font-semibold"
                                                        >
                                                            {formatPrice(
                                                                item.price,
                                                            )}
                                                        </ThemedText>
                                                        <ThemedText
                                                            size="xs"
                                                            variant="caption"
                                                        >
                                                            /{item.unit}
                                                        </ThemedText>
                                                    </div>
                                                </div>
                                            </CardBody>
                                        </Card>
                                    ))
                                ) : (
                                    <div className="col-span-full py-10">
                                        <EmptyState
                                            title="Tidak ada produk"
                                            description="Belum ada data produk tersedia."
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </CardBody>
                </Card>
            </div>

            {/* Right Side: Cart Summary (Desktop) */}
            <Card
                className="border-default-200 hidden h-auto w-full flex-col border md:flex md:h-full md:w-[350px] md:overflow-hidden"
                shadow="none"
            >
                <CardHeader className="border-default-200 flex items-center justify-between gap-4 border-b p-4">
                    <div className="flex items-center gap-2">
                        <LuShoppingCart size={18} />
                        <ThemedText className="font-semibold">
                            Keranjang Belanja
                        </ThemedText>
                    </div>
                    <Chip size="sm" color="primary" variant="flat">
                        {cart.length} Item
                    </Chip>
                </CardHeader>

                <CardBody className="flex flex-1 flex-col gap-2 overflow-y-auto p-4">
                    {cart.length === 0 ? (
                        <div className="flex h-full flex-col items-center justify-center gap-2">
                            <LuPackage size={40} className="text-default-400" />
                            <ThemedText size="sm" variant="secondary">
                                Keranjang kosong
                            </ThemedText>
                        </div>
                    ) : (
                        cart.map((item, index) => (
                            <Card
                                key={item.cart_id || index}
                                className="bg-default-50 dark:bg-default-100"
                                shadow="none"
                            >
                                <CardBody className="flex flex-col gap-2 p-3">
                                    <div className="flex justify-between">
                                        <div className="flex flex-col">
                                            <ThemedText
                                                size="sm"
                                                className="font-medium"
                                            >
                                                {item.alias || item.name}
                                            </ThemedText>
                                            <ThemedText
                                                size="xs"
                                                variant="caption"
                                            >
                                                {item.warehouse}
                                            </ThemedText>
                                        </div>
                                        <ThemedText
                                            size="sm"
                                            className="font-semibold"
                                        >
                                            {formatCurrency(item.subtotal)}
                                        </ThemedText>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div className="flex flex-col">
                                            <ThemedText
                                                size="xs"
                                                variant="secondary"
                                            >
                                                {formatThousands(item.quantity)}{" "}
                                                {item.unit} x{" "}
                                                {formatPrice(item.price)}
                                            </ThemedText>
                                        </div>
                                        <div className="flex gap-1">
                                            <Button
                                                isIconOnly
                                                size="sm"
                                                variant="light"
                                                onPress={() =>
                                                    handleEditCartItem(item)
                                                }
                                                className="h-6 w-6 min-w-0"
                                            >
                                                <LuPencil size={14} />
                                            </Button>
                                            <Button
                                                isIconOnly
                                                size="sm"
                                                variant="light"
                                                color="danger"
                                                onPress={() =>
                                                    handleRemoveFromCart(
                                                        item.cart_id,
                                                    )
                                                }
                                                className="h-6 w-6 min-w-0"
                                            >
                                                <LuTrash2 size={14} />
                                            </Button>
                                        </div>
                                    </div>
                                    {item.description && (
                                        <Card className="bg-warning-50">
                                            <CardBody className="px-2 py-1">
                                                <ThemedText
                                                    size="xs"
                                                    variant="warning"
                                                >
                                                    Note: {item.description}
                                                </ThemedText>
                                            </CardBody>
                                        </Card>
                                    )}
                                </CardBody>
                            </Card>
                        ))
                    )}
                </CardBody>

                <CardFooter className="border-default-200 bg-default-50 flex flex-col gap-4 border-t p-4">
                    <div className="flex w-full items-center justify-between">
                        <ThemedText size="sm" variant="secondary">
                            Total Bayar
                        </ThemedText>
                        <ThemedText
                            size="lg"
                            variant="primary"
                            className="font-bold"
                        >
                            {formatCurrency(totalAmount)}
                        </ThemedText>
                    </div>
                    <Button
                        color="primary"
                        className="w-full font-semibold"
                        onPress={handleSubmit(onSubmit)}
                        isLoading={storeSalesOrder.isLoading}
                        isDisabled={cart.length === 0}
                    >
                        Simpan Order
                    </Button>
                </CardFooter>
            </Card>

            {/* Mobile Fixed Footer */}
            <div className="bg-content1 border-default-200 fixed right-0 bottom-0 left-0 z-50 flex items-center justify-between border-t p-4 md:hidden">
                <div className="flex flex-col">
                    <ThemedText size="xs" variant="secondary">
                        Total {cart.length} Item
                    </ThemedText>
                    <ThemedText className="font-bold">
                        {formatCurrency(totalAmount)}
                    </ThemedText>
                </div>
                <Button
                    color="primary"
                    className="font-semibold"
                    size="sm"
                    onPress={cartDrawer.onOpen}
                    endContent={<LuShoppingCart />}
                >
                    Lihat Keranjang
                </Button>
            </div>

            {/* Mobile Cart Drawer */}
            <Drawer
                isOpen={cartDrawer.isOpen}
                onOpenChange={cartDrawer.onOpenChange}
                placement="bottom"
                className="h-[85vh]"
            >
                <DrawerContent>
                    {(onClose) => (
                        <>
                            <DrawerHeader className="flex flex-col gap-1">
                                <div className="flex items-center gap-2">
                                    <LuShoppingCart size={20} />
                                    <ThemedText className="font-semibold">
                                        Keranjang Belanja
                                    </ThemedText>
                                    <Chip
                                        size="sm"
                                        variant="flat"
                                        color="success"
                                    >
                                        {cart.length} Item
                                    </Chip>
                                </div>
                            </DrawerHeader>
                            <DrawerBody>
                                {cart.length === 0 ? (
                                    <div className="flex h-full flex-col items-center justify-center gap-2">
                                        <LuPackage
                                            size={40}
                                            className="text-default-400"
                                        />
                                        <ThemedText
                                            size="sm"
                                            variant="secondary"
                                        >
                                            Keranjang kosong
                                        </ThemedText>
                                    </div>
                                ) : (
                                    cart.map((item, index) => (
                                        <Card
                                            key={`${item.id}-${index}`}
                                            className="bg-default-50 dark:bg-default-100 shrink-0"
                                            shadow="none"
                                        >
                                            <CardBody className="flex flex-col gap-2 p-3">
                                                <div className="flex justify-between">
                                                    <ThemedText
                                                        size="sm"
                                                        className="font-medium"
                                                    >
                                                        {item.alias ||
                                                            item.name}
                                                    </ThemedText>
                                                    <ThemedText
                                                        size="sm"
                                                        className="font-semibold"
                                                    >
                                                        {formatCurrency(
                                                            item.subtotal,
                                                        )}
                                                    </ThemedText>
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <ThemedText
                                                        size="xs"
                                                        variant="secondary"
                                                    >
                                                        {formatThousands(
                                                            item.quantity,
                                                        )}{" "}
                                                        {item.unit} x{" "}
                                                        {formatPrice(
                                                            item.price,
                                                        )}
                                                    </ThemedText>
                                                    <div className="flex gap-1">
                                                        <Button
                                                            isIconOnly
                                                            size="sm"
                                                            variant="light"
                                                            onPress={() => {
                                                                handleEditCartItem(
                                                                    item,
                                                                );
                                                                onClose(); // Close drawer when editing
                                                            }}
                                                            className="h-6 w-6 min-w-0"
                                                        >
                                                            <LuPencil
                                                                size={14}
                                                            />
                                                        </Button>
                                                        <Button
                                                            isIconOnly
                                                            size="sm"
                                                            variant="light"
                                                            color="danger"
                                                            onPress={() =>
                                                                handleRemoveFromCart(
                                                                    item.id,
                                                                )
                                                            }
                                                            className="h-6 w-6 min-w-0"
                                                        >
                                                            <LuTrash2
                                                                size={14}
                                                            />
                                                        </Button>
                                                    </div>
                                                </div>
                                                {item.description && (
                                                    <Card className="bg-warning-50">
                                                        <CardBody className="px-2 py-1">
                                                            <ThemedText
                                                                size="xs"
                                                                variant="warning"
                                                            >
                                                                Note:{" "}
                                                                {
                                                                    item.description
                                                                }
                                                            </ThemedText>
                                                        </CardBody>
                                                    </Card>
                                                )}
                                            </CardBody>
                                        </Card>
                                    ))
                                )}
                            </DrawerBody>
                            <DrawerFooter className="flex-col gap-4">
                                <div className="flex w-full items-center justify-between">
                                    <ThemedText size="sm" variant="secondary">
                                        Total
                                    </ThemedText>
                                    <ThemedText
                                        variant="primary"
                                        className="font-bold"
                                    >
                                        {formatCurrency(totalAmount)}
                                    </ThemedText>
                                </div>
                                <Button
                                    color="primary"
                                    className="w-full font-semibold"
                                    onPress={handleSubmit(onSubmit)}
                                    isLoading={storeSalesOrder.isLoading}
                                    isDisabled={cart.length === 0}
                                >
                                    Simpan Order
                                </Button>
                            </DrawerFooter>
                        </>
                    )}
                </DrawerContent>
            </Drawer>

            {/* Item Detail Modal */}
            <Modal isOpen={itemModal.isOpen} onClose={itemModal.onClose}>
                <ModalContent>
                    {(onClose) => (
                        <form
                            onSubmit={handleItemSubmit(onItemSubmit)}
                            noValidate
                        >
                            <ModalBody className="pt-5">
                                <div className="flex gap-4">
                                    <div className="h-24 w-24 shrink-0">
                                        {selectedItem?.image_url ? (
                                            <Image
                                                src={selectedItem.image_url}
                                                alt={selectedItem?.name}
                                                className="aspect-square w-full rounded-lg object-cover"
                                            />
                                        ) : (
                                            <div className="bg-default-100 flex h-full w-full items-center justify-center rounded-lg">
                                                <NoImageFallback
                                                    iconSize={32}
                                                    icon={GiChicken}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex flex-col gap-0.5">
                                        <ThemedText className="font-semibold">
                                            {selectedItem?.name}
                                        </ThemedText>
                                        <ThemedText
                                            size="sm"
                                            variant="secondary"
                                        >
                                            Kategori: {selectedItem?.category}
                                        </ThemedText>
                                        {itemFormValues.warehouse_id && (
                                            <ThemedText
                                                size="sm"
                                                variant={
                                                    itemFormValues.available_stock >
                                                    0
                                                        ? "success"
                                                        : "danger"
                                                }
                                            >
                                                Stok tersedia:{" "}
                                                {itemFormValues.available_stock}{" "}
                                                {itemFormValues.unit}
                                            </ThemedText>
                                        )}
                                    </div>
                                </div>

                                {/* Alias */}
                                <Controller
                                    name="alias"
                                    control={itemControl}
                                    render={({ field }) => (
                                        <Input
                                            label="Alias"
                                            placeholder={
                                                selectedItem?.name || ""
                                            }
                                            variant="bordered"
                                            value={field.value}
                                            onValueChange={field.onChange}
                                            description="Kosongkan jika sama dengan nama produk."
                                        />
                                    )}
                                />

                                {/* Row 1: Satuan & Jumlah */}
                                <div className="border-default-300 grid grid-cols-2 gap-4 border-t border-dashed pt-4">
                                    {/* Unit Selection */}
                                    <Controller
                                        name="unit_id"
                                        control={itemControl}
                                        rules={{
                                            required: "Satuan wajib dipilih",
                                        }}
                                        render={({ field }) => (
                                            <Autocomplete
                                                label="Satuan"
                                                placeholder="Pilih satuan"
                                                variant="bordered"
                                                selectedKey={
                                                    field.value
                                                        ? String(field.value)
                                                        : null
                                                }
                                                onSelectionChange={(val) => {
                                                    field.onChange(val);
                                                    handleUnitChange(val);
                                                }}
                                                isDisabled={
                                                    !selectedItem?.units?.length
                                                }
                                                isRequired
                                                isInvalid={!!itemErrors.unit_id}
                                                errorMessage={
                                                    itemErrors.unit_id?.message
                                                }
                                            >
                                                {(
                                                    selectedItem?.units || []
                                                ).map((u) => (
                                                    <AutocompleteItem
                                                        key={String(u.unit_id)}
                                                        value={u.unit_id}
                                                        textValue={u.unit}
                                                    >
                                                        <div className="flex items-center justify-between gap-2">
                                                            <span>
                                                                {u.unit}
                                                            </span>
                                                            {u.is_default ? (
                                                                <Chip
                                                                    size="sm"
                                                                    color="primary"
                                                                    variant="flat"
                                                                >
                                                                    Default
                                                                </Chip>
                                                            ) : null}
                                                        </div>
                                                    </AutocompleteItem>
                                                ))}
                                            </Autocomplete>
                                        )}
                                    />

                                    <Controller
                                        name="quantity"
                                        control={itemControl}
                                        rules={{
                                            required: "Jumlah wajib diisi",
                                            min: {
                                                value: 1,
                                                message: "Minimal 1",
                                            },
                                        }}
                                        render={({ field }) => (
                                            <NumberInput
                                                label="Jumlah"
                                                placeholder="0"
                                                variant="bordered"
                                                minValue={1}
                                                value={field.value}
                                                onValueChange={(val) => {
                                                    field.onChange(val);
                                                    handleQuantityValueChange(
                                                        val,
                                                    );
                                                }}
                                                hideStepper
                                                isClearable
                                                isWheelDisabled
                                                isRequired
                                                isInvalid={
                                                    !!itemErrors.quantity
                                                }
                                                errorMessage={
                                                    itemErrors.quantity?.message
                                                }
                                                endContent={
                                                    <ThemedText
                                                        size="sm"
                                                        variant="caption"
                                                    >
                                                        {itemFormValues.unit}
                                                    </ThemedText>
                                                }
                                            />
                                        )}
                                    />
                                </div>

                                {/* Row 2: Pilih Gudang */}
                                <Controller
                                    name="warehouse_id"
                                    control={itemControl}
                                    rules={{ required: "Gudang wajib dipilih" }}
                                    render={({ field }) => {
                                        return (
                                            <Autocomplete
                                                label="Gudang"
                                                placeholder="Pilih gudang"
                                                variant="bordered"
                                                isLoading={
                                                    fetchWarehouseStock.isLoading
                                                }
                                                selectedKey={
                                                    field.value
                                                        ? String(field.value)
                                                        : null
                                                }
                                                onSelectionChange={(val) => {
                                                    field.onChange(val);
                                                    handleWarehouseChange(val);
                                                }}
                                                isDisabled={
                                                    !itemFormValues.unit_id
                                                }
                                                isRequired
                                                isInvalid={
                                                    !!itemErrors.warehouse_id
                                                }
                                                errorMessage={
                                                    itemErrors.warehouse_id
                                                        ?.message
                                                }
                                            >
                                                {(
                                                    fetchWarehouseStock.data ||
                                                    []
                                                ).map((w) => {
                                                    const realStock =
                                                        getRealStock(
                                                            w.value,
                                                            w.stock,
                                                        );
                                                    return (
                                                        <AutocompleteItem
                                                            key={String(
                                                                w.value,
                                                            )}
                                                            value={w.value}
                                                            textValue={w.title}
                                                        >
                                                            <div className="flex items-center justify-between gap-2">
                                                                <span>
                                                                    {w.title}
                                                                </span>
                                                                <Chip
                                                                    size="sm"
                                                                    variant="flat"
                                                                    color={
                                                                        realStock >
                                                                        0
                                                                            ? "success"
                                                                            : "danger"
                                                                    }
                                                                >
                                                                    {realStock}{" "}
                                                                    {
                                                                        itemFormValues.unit
                                                                    }
                                                                </Chip>
                                                            </div>
                                                        </AutocompleteItem>
                                                    );
                                                })}
                                            </Autocomplete>
                                        );
                                    }}
                                />

                                {/* Row 3: Harga Satuan */}
                                <div className="grid grid-cols-1 gap-4">
                                    <Controller
                                        name="price"
                                        control={itemControl}
                                        rules={{
                                            required: "Harga wajib diisi",
                                        }}
                                        render={({ field }) => (
                                            <NumberInput
                                                label="Harga Satuan"
                                                placeholder="0"
                                                variant="bordered"
                                                minValue={0}
                                                value={field.value}
                                                onValueChange={(val) => {
                                                    field.onChange(val);
                                                    handlePriceValueChange(val);
                                                }}
                                                hideStepper
                                                isClearable
                                                isWheelDisabled
                                                isRequired
                                                isInvalid={!!itemErrors.price}
                                                errorMessage={
                                                    itemErrors.price?.message
                                                }
                                                startContent={
                                                    <ThemedText
                                                        size="sm"
                                                        variant="secondary"
                                                    >
                                                        Rp
                                                    </ThemedText>
                                                }
                                                formatOptions={
                                                    PRICE_FORMAT_OPTIONS
                                                }
                                                description="Harga dapat disesuaikan. Gunakan koma untuk desimal (mis. 333,33)."
                                            />
                                        )}
                                    />
                                    {isLivestock && (
                                        <div className="col-span-2">
                                            <Controller
                                                name="total_birds"
                                                control={itemControl}
                                                render={({ field }) => (
                                                    <Input
                                                        type="number"
                                                        label="Jumlah Ekor"
                                                        placeholder="Contoh: 20"
                                                        variant="bordered"
                                                        value={field.value}
                                                        onValueChange={
                                                            field.onChange
                                                        }
                                                        endContent={
                                                            <ThemedText
                                                                size="sm"
                                                                variant="caption"
                                                            >
                                                                Ekor
                                                            </ThemedText>
                                                        }
                                                        description="Informasi ini akan disimpan di catatan item."
                                                    />
                                                )}
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Row 4: Subtotal (sumber kebenaran nilai baris) */}
                                <div className="border-default-300 mt-4 border-t border-dashed pt-4">
                                    <Controller
                                        name="subtotal"
                                        control={itemControl}
                                        rules={{
                                            required: "Subtotal wajib diisi",
                                            validate: (val) =>
                                                Number.isFinite(val) ||
                                                "Subtotal wajib diisi",
                                            min: {
                                                value: 0,
                                                message: "Minimal 0",
                                            },
                                        }}
                                        render={({ field }) => (
                                            <NumberInput
                                                label="Subtotal"
                                                placeholder="0"
                                                variant="bordered"
                                                minValue={0}
                                                value={field.value}
                                                onValueChange={(val) => {
                                                    field.onChange(val);
                                                    handleSubtotalValueChange(
                                                        val,
                                                    );
                                                }}
                                                hideStepper
                                                isClearable
                                                isWheelDisabled
                                                isRequired
                                                isInvalid={
                                                    !!itemErrors.subtotal
                                                }
                                                errorMessage={
                                                    itemErrors.subtotal?.message
                                                }
                                                startContent={
                                                    <ThemedText
                                                        size="sm"
                                                        variant="secondary"
                                                    >
                                                        Rp
                                                    </ThemedText>
                                                }
                                                formatOptions={
                                                    SUBTOTAL_FORMAT_OPTIONS
                                                }
                                                description="Isi langsung total baris bila harga per satuan tidak bulat (mis. 3 butir Rp 1.000)."
                                            />
                                        )}
                                    />
                                </div>
                            </ModalBody>
                            <ModalFooter>
                                <Button
                                    variant="light"
                                    color="danger"
                                    size="sm"
                                    onPress={onClose}
                                >
                                    Batal
                                </Button>
                                <Button color="primary" size="sm" type="submit">
                                    Tambah ke Keranjang
                                </Button>
                            </ModalFooter>
                        </form>
                    )}
                </ModalContent>
            </Modal>
        </div>
    );
};

export default SalesOrderCreate;
